import { BarChart3, ListChecks, Plus, Settings, Target, type LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';

export type MetasSeccion = 'panel' | 'listado' | 'reportes' | 'configuracion';

interface Enlace {
  seccion: MetasSeccion;
  to: string;
  label: string;
  icon: LucideIcon;
}

/** Orden del submenú de metas (coincide con el catálogo de módulos). */
const ENLACES: readonly Enlace[] = [
  { seccion: 'panel', to: '/metas', label: 'Panel', icon: Target },
  { seccion: 'listado', to: '/metas/listado', label: 'Listado', icon: ListChecks },
  { seccion: 'reportes', to: '/metas/reportes', label: 'Reportes', icon: BarChart3 },
  { seccion: 'configuracion', to: '/metas/configuracion', label: 'Configuración', icon: Settings },
];

const BASE =
  'inline-flex shrink-0 items-center gap-2 rounded-control px-3 py-2 text-xs font-semibold transition-colors';
const INACTIVO = 'border border-ink-200 bg-white text-ink-700 hover:border-brand-300 hover:text-brand-700';
const ACTIVO = 'bg-ink-900 text-white';

interface MetasNavProps {
  /** Sección actualmente visible; se resalta en la barra. */
  active: MetasSeccion;
  /** Oculta el acceso directo al formulario de creación. */
  sinNueva?: boolean;
  className?: string;
}

/**
 * Submenú del módulo de metas.
 *
 * Se repite en todas las pantallas para poder saltar entre panel, listado,
 * reportes y configuración sin volver al menú lateral.
 */
export function MetasNav({ active, sinNueva = false, className }: MetasNavProps) {
  return (
    <nav className={cn('flex items-center gap-2 overflow-x-auto pb-1 sm:flex-wrap sm:pb-0', className)} aria-label="Secciones de metas">
      {ENLACES.map((enlace) => (
        <Link
          key={enlace.seccion}
          to={enlace.to}
          aria-current={enlace.seccion === active ? 'page' : undefined}
          className={cn(BASE, enlace.seccion === active ? ACTIVO : INACTIVO)}
        >
          <enlace.icon className="size-3.5" aria-hidden="true" />
          {enlace.label}
        </Link>
      ))}

      {sinNueva ? null : (
        <Link to="/metas/nueva" className={cn(BASE, 'bg-brand-600 text-white hover:bg-brand-700')}>
          <Plus className="size-3.5" aria-hidden="true" />
          Nueva meta
        </Link>
      )}
    </nav>
  );
}
